import { useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AssessmentIcon from "@mui/icons-material/Assessment";
import { createCompany, updateCompany, deleteCompany } from "../axios/axiosFunctions";
import TeamsSection from "./TeamsSection";
import DeleteConfirmationModal from "./common/DeleteConfirmationModal";
import { slugForCategory } from "../utils/timFamily";

const CompaniesTIM = ({ companies, callCompanies, loading, category }) => {
  const [openCompanyDialog, setOpenCompanyDialog] = useState(false);
  const [newCompany, setNewCompany] = useState("");
  const [alertSeverity, setAlertSeverity] = useState("error");
  const [alertMsg, setAlertMsg] = useState("");
  const [alertOpen, setAlertOpen] = useState(false);

  // Edit company state
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [editCompany, setEditCompany] = useState({ id: "", name: "" });

  // Delete company state
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [companyToDelete, setCompanyToDelete] = useState({ id: "", name: "" });

  const handleDialogClose = () => {
    setOpenCompanyDialog(false);
    setNewCompany("");
  };

  const handleCompanyChange = (e) => {
    const { value } = e.target;
    setNewCompany(value);
  };

  const handleCreateCompany = async () => {
    if (newCompany === "") {
      setAlertMsg("Please fill the name");
      setAlertSeverity("error");
      setAlertOpen(true);
      setTimeout(() => {
        setAlertOpen(false);
      }, 2000);
      return;
    }

    // Check if company name already exists (case-insensitive)
    const companyExists = companies.some(
      (c) => c.name.toLowerCase() === newCompany.toLowerCase(),
    );
    if (companyExists) {
      setAlertMsg("A company with this name already exists");
      setAlertSeverity("error");
      setAlertOpen(true);
      setTimeout(() => {
        setAlertOpen(false);
      }, 2000);
      return;
    }

    const data = {
      name: newCompany,
      category,
    };

    const res = await createCompany(data);
    if (!res) {
      setAlertMsg(
        "Error while creating the company, please try later o contact admin",
      );
      setAlertSeverity("error");
      setAlertOpen(true);
      setTimeout(() => {
        setAlertOpen(false);
      }, 2000);
    }

    setAlertMsg("Company Created");
    setAlertSeverity("success");
    setAlertOpen(true);
    setTimeout(() => {
      callCompanies();
      setAlertOpen(false);
      handleDialogClose();
    }, 2000);
  };

  // Edit company handlers
  const handleOpenEditDialog = (e, company) => {
    e.stopPropagation();
    setEditCompany({ id: company._id, name: company.name });
    setOpenEditDialog(true);
  };

  const handleEditDialogClose = () => {
    setOpenEditDialog(false);
    setEditCompany({ id: "", name: "" });
  };

  const handleEditCompanyChange = (e) => {
    setEditCompany({ ...editCompany, name: e.target.value });
  };

  const handleUpdateCompany = async () => {
    if (editCompany.name === "") {
      setAlertMsg("Please fill the name");
      setAlertSeverity("error");
      setAlertOpen(true);
      setTimeout(() => {
        setAlertOpen(false);
      }, 2000);
      return;
    }

    // Check if company name already exists (case-insensitive), excluding current company
    const companyExists = companies.some(
      (c) => c.name.toLowerCase() === editCompany.name.toLowerCase() && c._id !== editCompany.id,
    );
    if (companyExists) {
      setAlertMsg("A company with this name already exists");
      setAlertSeverity("error");
      setAlertOpen(true);
      setTimeout(() => {
        setAlertOpen(false);
      }, 2000);
      return;
    }

    const res = await updateCompany(editCompany.id, { name: editCompany.name });
    if (!res) {
      setAlertMsg("Error while updating the company, please try later or contact admin");
      setAlertSeverity("error");
      setAlertOpen(true);
      setTimeout(() => {
        setAlertOpen(false);
      }, 2000);
      return;
    }

    setAlertMsg("Company Updated");
    setAlertSeverity("success");
    setAlertOpen(true);
    setTimeout(() => {
      callCompanies();
      setAlertOpen(false);
      handleEditDialogClose();
    }, 2000);
  };

  // Delete company handlers
  const handleOpenDeleteDialog = (e, company) => {
    e.stopPropagation();
    setCompanyToDelete({ id: company._id, name: company.name });
    setOpenDeleteDialog(true);
  };

  const handleDeleteDialogClose = () => {
    setOpenDeleteDialog(false);
    setCompanyToDelete({ id: "", name: "" });
  };

  const handleDeleteCompany = async () => {
    const res = await deleteCompany(companyToDelete.id);
    if (!res) {
      setAlertMsg("Error while deleting the company, please try later or contact admin");
      setAlertSeverity("error");
      setAlertOpen(true);
      setTimeout(() => {
        setAlertOpen(false);
      }, 2000);
      handleDeleteDialogClose();
      return;
    }

    setAlertMsg("Company Deleted");
    setAlertSeverity("success");
    setAlertOpen(true);
    setTimeout(() => {
      callCompanies();
      setAlertOpen(false);
      handleDeleteDialogClose();
    }, 2000);
  };

  return (
    <Box sx={{ marginTop: "40px", marginBottom: "100px" }}>
      <Typography variant="h4" fontWeight={500} color="secondary">
        Companies
      </Typography>
      <Button
        onClick={() => setOpenCompanyDialog(true)}
        variant="contained"
        sx={{ color: "white", marginTop: "20px", marginBottom: "20px" }}
        endIcon={<AddCircleOutlineIcon />}
      >
        ADD COMPANY
      </Button>
      {alertOpen && (
        <Alert sx={{ marginBottom: "20px" }} severity={alertSeverity}>
          {alertMsg}
        </Alert>
      )}
      {!loading && (
        <>
          {companies.length === 0 ? (
            <Typography variant="h6">No companies</Typography>
          ) : (
            <>
              {companies.map((c) => (
                <Accordion key={c._id} sx={{ marginBottom: "20px" }}>
                  <AccordionSummary>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
                      <Typography variant="h5">{c.name}</Typography>
                      <Box>
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            window.open(`/${slugForCategory(category)}/company/${c._id}/report`, "_blank");
                          }}
                          color="secondary"
                          size="small"
                          sx={{ mr: 1 }}
                          title="Company-level report"
                        >
                          <AssessmentIcon />
                        </IconButton>
                        <IconButton
                          onClick={(e) => handleOpenEditDialog(e, c)}
                          color="primary"
                          size="small"
                          sx={{ mr: 1 }}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          onClick={(e) => handleOpenDeleteDialog(e, c)}
                          color="error"
                          size="small"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <TeamsSection companyId={c._id} category={category} />
                  </AccordionDetails>
                </Accordion>
              ))}
            </>
          )}
        </>
      )}

      {/* Create Company Dialog */}
      <Dialog
        open={openCompanyDialog}
        onClose={handleDialogClose}
        maxWidth="md"
        fullWidth
      >
        <DialogContent>
          <Typography variant="h6" sx={{ textAlign: "center" }}>
            Create Company
          </Typography>
          <Typography variant="subtitle1">Company Name</Typography>
          <TextField onChange={handleCompanyChange} fullWidth />
          {alertOpen && (
            <Alert sx={{ marginTop: "20px" }} severity={alertSeverity}>
              {alertMsg}
            </Alert>
          )}
          <Box
            sx={{ display: "flex", margin: "20px", justifyContent: "center" }}
          >
            <Button
              onClick={handleCreateCompany}
              variant="contained"
              sx={{ marginRight: "20px", justifyContent: "space-around" }}
            >
              CREATE
            </Button>
            <Button
              variant="contained"
              onClick={handleDialogClose}
              color="customGray"
            >
              CANCEL
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Edit Company Dialog */}
      <Dialog
        open={openEditDialog}
        onClose={handleEditDialogClose}
        maxWidth="md"
        fullWidth
      >
        <DialogContent>
          <Typography variant="h6" sx={{ textAlign: "center" }}>
            Edit Company
          </Typography>
          <Typography variant="subtitle1">Company Name</Typography>
          <TextField
            value={editCompany.name}
            onChange={handleEditCompanyChange}
            fullWidth
          />
          {alertOpen && (
            <Alert sx={{ marginTop: "20px" }} severity={alertSeverity}>
              {alertMsg}
            </Alert>
          )}
          <Box
            sx={{ display: "flex", margin: "20px", justifyContent: "center" }}
          >
            <Button
              onClick={handleUpdateCompany}
              variant="contained"
              sx={{ marginRight: "20px" }}
            >
              UPDATE
            </Button>
            <Button
              variant="contained"
              onClick={handleEditDialogClose}
              color="customGray"
            >
              CANCEL
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Delete Company Confirmation Dialog */}
      <DeleteConfirmationModal
        openDialog={openDeleteDialog}
        closeDialog={handleDeleteDialogClose}
        title={`Delete Company: ${companyToDelete.name}`}
        message="If you delete the company all teams will be deleted as well."
        onConfirm={handleDeleteCompany}
      />
    </Box>
  );
};

export default CompaniesTIM;
